/**
 * DataTable — operations-grade data table.
 * 40px rows, warm tinted header, tabular-nums on numeric cols, sticky header.
 *
 * Usage:
 *   <DataTable
 *     columns={[
 *       { key: 'invoice', label: 'Invoice', numeric: false },
 *       { key: 'amount',  label: 'Amount',  numeric: true, tone: 'neutral' },
 *       { key: 'status',  label: 'Status',  align: 'center' },
 *     ]}
 *     rows={data}
 *     renderCell={(row, col) => row[col.key]}
 *     footer={<div>Total: ₱48,250</div>}
 *     emptyState={<EmptyState ... />}
 *   />
 */

export function DataTable({
  columns = [],
  rows = [],
  renderCell,
  footer,
  emptyState,
  stickyHeader = true,
  className = '',
  onRowClick,
}) {
  return (
    <div
      className={`rounded-lg overflow-hidden ${className}`}
      style={{ border: '1px solid var(--border-default)' }}
    >
      <div className={`overflow-x-auto ${stickyHeader ? 'overflow-y-auto' : ''}`}>
        <table className="w-full text-[13px]">
          <thead>
            <tr style={{ backgroundColor: '#F1F0ED', borderBottom: '1px solid var(--border-default)' }}>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`h-9 px-3 font-semibold text-[11px] uppercase tracking-wide whitespace-nowrap ${
                    col.numeric || col.align === 'right' ? 'text-right' : ''
                  } ${col.align === 'center' ? 'text-center' : ''} ${
                    !col.numeric && col.align !== 'right' && col.align !== 'center' ? 'text-left' : ''
                  } ${stickyHeader ? 'sticky top-0 z-10' : ''}`}
                  style={{
                    color: 'var(--text-secondary)',
                    backgroundColor: '#F1F0ED',
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 && emptyState ? (
              <tr>
                <td colSpan={columns.length} className="p-0">
                  {emptyState}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr
                  key={row.id || row._id || i}
                  className={`transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={{
                    borderBottom: '1px solid var(--border-subtle)',
                    transitionDuration: 'var(--ease-fast)',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--surface-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`h-10 px-3 ${
                        col.numeric || col.align === 'right' ? 'text-right' : ''
                      } ${col.align === 'center' ? 'text-center' : ''}`}
                      style={
                        col.numeric
                          ? {
                              fontVariantNumeric: 'tabular-nums',
                              fontFeatureSettings: '"tnum"',
                              fontWeight: 500,
                              color: col.tone === 'positive'
                                ? 'var(--num-positive)'
                                : col.tone === 'negative'
                                ? 'var(--num-negative)'
                                : 'var(--text-primary)',
                            }
                          : { color: 'var(--text-primary)' }
                      }
                    >
                      {renderCell ? renderCell(row, col) : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>

          {footer && (
            <tfoot>
              <tr style={{ borderTop: '1px solid var(--border-strong)', backgroundColor: '#F1F0ED' }}>
                <td colSpan={columns.length} className="px-3 py-2.5">
                  {footer}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
